import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { LogOut, RefreshCw, Search, X, Plus, User as UserIcon } from 'lucide-react';

import AgentKpiStrip from '@/components/AgentDashboard/AgentKpiStrip';
import AgentCaseTable from '@/components/AgentDashboard/AgentCaseTable';
import NextActionsStrip from '@/components/AgentDashboard/NextActionsStrip';
import DemoResetButton from '@/components/DemoResetButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'drafts', label: 'Drafts', kpi: 'drafts_in_progress', testid: 'agent-tab-drafts' },
  { key: 'returned', label: 'Returned', kpi: 'returned_cases', testid: 'agent-tab-returned' },
  { key: 'ready', label: 'Ready to Submit', kpi: 'ready_to_submit', testid: 'agent-tab-ready' },
  { key: 'missing_docs', label: 'Missing Documents', kpi: 'missing_documents', testid: 'agent-tab-missing-docs' },
  { key: 'pending_ext', label: 'Pending Ext. Refresh', kpi: 'pending_external_refresh', testid: 'agent-tab-pending-ext' },
  { key: 'submitted', label: 'Recent Submissions', testid: 'agent-tab-submitted' },
];

const JOURNEY_LABELS = {
  SAME_LIMIT_RENEWAL: 'Same Limit Renewal',
  ENHANCED_LIMIT: 'Enhanced Limit',
  NEW_FARMER: 'New Farmer',
};

const AgentDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('drafts');
  const [tabInitialized, setTabInitialized] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filters, setFilters] = useState({ q: '', district: null, journey_type: null, status: null, readiness: null });
  const [loading, setLoading] = useState(true);

  const loadFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/filters`);
      setDistricts(res.data.districts || []);
    } catch { /* ignore */ }
  }, []);

  const loadKpis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/agent/kpis`);
      setKpis(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied');
        navigate('/officer/inbox');
      }
    }
  }, [navigate]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        tab,
        district: filters.district || undefined,
        journey_type: filters.journey_type || undefined,
        status: filters.status || undefined,
        readiness: filters.readiness || undefined,
        q: filters.q || undefined,
        limit: 100,
      };
      const res = await axios.get(`${API}/dashboard/agent/cases`, { params });
      setRows(res.data.cases || []);
    } catch {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { loadCases(); }, [loadCases]);

  // Smart default tab once KPIs are loaded (most urgent pile first)
  useEffect(() => {
    if (!kpis || tabInitialized) return;
    let defaultTab = 'drafts';
    if ((kpis.returned_cases || 0) > 0) defaultTab = 'returned';
    else if ((kpis.ready_to_submit || 0) > 0) defaultTab = 'ready';
    else if ((kpis.missing_documents || 0) > 0) defaultTab = 'missing_docs';
    setTab(defaultTab);
    setTabInitialized(true);
  }, [kpis, tabInitialized]);

  const handleClear = () => setFilters({ q: '', district: null, journey_type: null, status: null, readiness: null });
  const activeCount = Object.values(filters).filter(v => v && v !== 'all').length;

  const handleRefresh = () => {
    loadKpis();
    loadCases();
    toast.success('Dashboard refreshed');
  };

  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '30px' }} />
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-none" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Agent Dashboard
                </h1>
                <div className="text-[10px] text-gray-500 mt-0.5">Track incomplete, returned, and ready-to-submit farmer assessments · {todayLabel}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 px-2.5 py-1 bg-slate-100 rounded text-[11px]">
                <UserIcon className="h-3 w-3 text-slate-600" />
                <span className="text-slate-700 font-medium">{user?.email}</span>
                <Badge className="text-[9px] border-none bg-green-100 text-green-800">{user?.role}</Badge>
              </div>
              <Button
                size="sm"
                className="h-8"
                style={{ background: '#1e40af' }}
                onClick={() => navigate('/assessment/new')}
                data-testid="agent-new-assessment-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> New Assessment
              </Button>
              <Button variant="ghost" size="sm" onClick={handleRefresh} className="h-8" data-testid="agent-refresh-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
              <DemoResetButton role={user?.role} onDone={handleRefresh} />
              <Button variant="outline" size="sm" onClick={onLogout} className="h-8" data-testid="agent-logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto px-4 lg:px-6 py-5">
        <NextActionsStrip kpis={kpis} activeTab={tab} onSelect={(t) => { setTab(t); setTabInitialized(true); }} />
        <AgentKpiStrip data={kpis} loading={!kpis} />

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white border border-gray-200 rounded-md" data-testid="agent-filter-bar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input
              className="h-9 text-sm pl-8"
              placeholder="Search by farmer name or Aadhaar last 4..."
              value={filters.q || ''}
              onChange={e => setFilters({ ...filters, q: e.target.value })}
              data-testid="agent-search-input"
            />
          </div>
          <select
            className="h-9 text-sm border border-gray-300 rounded-md px-2 bg-white min-w-[140px]"
            value={filters.district || 'all'}
            onChange={e => setFilters({ ...filters, district: e.target.value === 'all' ? null : e.target.value })}
            data-testid="agent-filter-district"
          >
            <option value="all">All Districts</option>
            {districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            className="h-9 text-sm border border-gray-300 rounded-md px-2 bg-white min-w-[160px]"
            value={filters.journey_type || 'all'}
            onChange={e => setFilters({ ...filters, journey_type: e.target.value === 'all' ? null : e.target.value })}
            data-testid="agent-filter-journey"
          >
            <option value="all">All Journeys</option>
            {Object.entries(JOURNEY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            className="h-9 text-sm border border-gray-300 rounded-md px-2 bg-white min-w-[140px]"
            value={filters.readiness || 'all'}
            onChange={e => setFilters({ ...filters, readiness: e.target.value === 'all' ? null : e.target.value })}
            data-testid="agent-filter-readiness"
          >
            <option value="all">All Readiness</option>
            <option value="READY">Ready</option>
            <option value="NOT_READY">Not Ready</option>
          </select>
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={handleClear} data-testid="agent-clear-filters-btn">
              <X className="h-3 w-3 mr-1" /> Clear ({activeCount})
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-3 border-b border-gray-200 overflow-x-auto">
          {TABS.map(t => {
            const active = tab === t.key;
            const count = t.kpi && kpis ? kpis[t.kpi] : null;
            return (
              <button
                key={t.key}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap relative ${active ? 'text-blue-700' : 'text-gray-500 hover:text-gray-800'}`}
                onClick={() => setTab(t.key)}
                data-testid={t.testid}
              >
                {t.label}
                {count != null && (
                  <Badge className="ml-2 text-[10px] border-none bg-slate-100 text-slate-700 tabular-nums">{count}</Badge>
                )}
                {active && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-700" />}
              </button>
            );
          })}
          <div className="ml-auto text-[11px] text-gray-400 pr-2 self-center">
            {rows.length} {rows.length === 1 ? 'case' : 'cases'}
          </div>
        </div>

        <AgentCaseTable rows={rows} tab={tab} loading={loading} />
      </main>
    </div>
  );
};

export default AgentDashboard;
